import _ from "underts";
function applyRankToParents(parents, now) {
    if (parents.length === 1) {
        return [applyRankToSoloParent(parents[0])];
    }
    if (parents.length === 2) {
        return applyRankToParentsCouple(parents);
    }
    console.warn("applyFamilyRank: More than 2 parents detected, not applying family ranks");
    return parents;
}
function applyRankToParentsCouple(group) {
    return group.map((p) => ({ ...p, familly_rank: "PARENT" }));
}
function applyRankToSoloParent(parent) {
    return {
        ...parent,
        familly_rank: "PARENT",
    };
}
export function applyFamilyRank(patients, now) {
    const ageGroups = groupByBirthdateProximity(patients, 1000 * 60 * 60 * 24 * 30 * 12 * 15); // 15 years threshold
    if (ageGroups.length === 0) {
        return patients;
    }
    if (ageGroups.length === 1) {
        const group = _.first(ageGroups);
        if (!group) {
            throw new Error("Unexpected empty group");
        }
        if (areProbablyChildren(group, now)) {
            return group.map((p) => ({
                ...p,
                familly_rank: "CHILD",
            }));
        }
        if (areProbablyParents(group, now)) {
            if (group.length === 1) {
                return group.map((p) => ({
                    ...p,
                    familly_rank: "PARENT",
                }));
            }
            if (group.length === 2) {
                return applyRankToParentsCouple(group);
            }
            console.warn("applyFamilyRank: More than 2 parents detected, not applying family ranks");
            return patients;
        }
        return patients;
    }
    if (ageGroups.length === 2) {
        const [maybeParents, maybeChildren] = ageGroups;
        return [
            ...applyRankToParents(maybeParents, now),
            ...maybeChildren.map((p) => ({ ...p, familly_rank: "CHILD" })),
        ];
    }
    if (ageGroups.length === 3) {
        const [maybeGrandParents, maybeParents, maybeChildren] = ageGroups;
        if (maybeGrandParents.every((p) => isPerhapsGrandParent(p, now)) &&
            areProbablyParents(maybeParents, now) &&
            areProbablyChildren(maybeChildren, now)) {
            return [
                ...maybeGrandParents.map((p) => ({
                    ...p,
                    familly_rank: "GRANDPARENT",
                })),
                ...applyRankToParents(maybeParents, now),
                ...maybeChildren.map((p) => ({ ...p, familly_rank: "CHILD" })),
            ];
        }
        return patients;
    }
    // More than 3 groups, we cannot determine family ranks reliably
    return patients;
}
function birthdateToDate(birthdate) {
    const [day, month, year] = birthdate.split("/").map(Number);
    return new Date(year, month - 1, day);
}
function computeDistance(a, b) {
    const dateA = birthdateToDate(a.birthdate);
    const dateB = birthdateToDate(b.birthdate);
    return Math.abs(dateA.getTime() - dateB.getTime());
}
function groupByBirthdateProximity(patients, threshold) {
    const sorted = patients.sort((a, b) => {
        const dateA = birthdateToDate(a.birthdate);
        const dateB = birthdateToDate(b.birthdate);
        return dateA.getTime() - dateB.getTime();
    });
    const windows = _.windows(sorted, 2);
    const withDistance = windows.map(([a, b]) => ({
        pair: [a, b],
        distance: computeDistance(a, b),
    }));
    const groups = [];
    let currentGroup = [];
    for (const { pair, distance } of withDistance) {
        if (distance >= threshold) {
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
                currentGroup = [pair[1]];
                continue;
            }
            else {
                groups.push([pair[0]]);
                currentGroup = [pair[1]];
                continue;
            }
        }
        if (currentGroup.length === 0) {
            currentGroup.push(pair[0]);
            currentGroup.push(pair[1]);
        }
        else {
            currentGroup.push(pair[1]);
        }
    }
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    return groups;
}
function isPerhapsParent(a, now) {
    const age = now.getFullYear() - birthdateToDate(a.birthdate).getFullYear();
    return age >= 26;
}
function isPerhapsGrandParent(a, now) {
    const age = now.getFullYear() - birthdateToDate(a.birthdate).getFullYear();
    return age >= 60;
}
function areProbablyParents(group, now) {
    return group.every((p) => isPerhapsParent(p, now));
}
function areProbablyChildren(group, now) {
    return group.every((p) => !isPerhapsParent(p, now));
}
//# sourceMappingURL=applyFamilyRank.js.map